import type { TimelineData, TimelineEditorState } from '@/features/timeline';
import {
  TimelineConflictError,
  TimelineNotFoundError,
} from '@/features/timeline/application/timeline-errors';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

type TimelineDetailErrorCode = 'not-found' | 'error' | null;

type OnOptimistic = (state: TimelineEditorState) => void;

interface TimelineEditorActionUseCases {
  addSection: (id: string, onOptimistic?: OnOptimistic) => Promise<TimelineEditorState | null>;
  removeSection: (id: string, sectionId: string, onOptimistic?: OnOptimistic) => Promise<TimelineEditorState | null>;
  moveSection: (id: string, sectionId: string, toIndex: number, onOptimistic?: OnOptimistic) => Promise<TimelineEditorState | null>;
  updateSectionLabel: (
    id: string,
    sectionId: string,
    nextLabel: string,
    onOptimistic?: OnOptimistic,
  ) => Promise<TimelineEditorState | null>;
  updateItemTitle: (
    id: string,
    sectionId: string,
    itemId: string,
    nextTitle: string,
    onOptimistic?: OnOptimistic,
  ) => Promise<TimelineEditorState | null>;
  createItem: (
    id: string,
    sectionId: string,
    draft: { title: string; startDate: string; endDate?: string },
    onOptimistic?: OnOptimistic,
  ) => Promise<TimelineEditorState | null>;
  changeItemDateRange: (
    id: string,
    itemId: string,
    nextRange: { sectionId: string; startDate: string; endDate?: string },
    onOptimistic?: OnOptimistic,
  ) => Promise<TimelineEditorState | null>;
  hideSection: (
    id: string,
    sectionId: string,
    onOptimistic?: OnOptimistic,
  ) => Promise<TimelineEditorState | null>;
  showSection: (
    id: string,
    sectionId: string,
    onOptimistic?: OnOptimistic,
  ) => Promise<TimelineEditorState | null>;
  deleteItem: (
    id: string,
    sectionId: string,
    itemId: string,
    onOptimistic?: OnOptimistic,
  ) => Promise<TimelineEditorState | null>;
  undo: (id: string, onOptimistic?: OnOptimistic) => Promise<TimelineEditorState | null>;
  redo: (id: string, onOptimistic?: OnOptimistic) => Promise<TimelineEditorState | null>;
}

interface UseTimelineEditorActionsParams {
  timelineId: string | undefined;
  timelineData: TimelineData | null;
  canUndo: boolean;
  canRedo: boolean;
  applyEditorState: (nextState: TimelineEditorState | null) => void;
  setError: (error: string | null) => void;
  setErrorCode: (code: TimelineDetailErrorCode) => void;
  setTimelineData: (data: TimelineData | null) => void;
  useCases: TimelineEditorActionUseCases;
}

export interface TimelineEditorActions {
  addTimelineSection: () => void;
  handleSectionDelete: (sectionId: string) => void;
  handleSectionMove: (sectionId: string, toIndex: number) => void;
  handleSectionLabelChange: (sectionId: string, nextLabel: string) => void;
  handleSectionVisibilityToggle: (sectionId: string) => void;
  handleItemTitleChange: (sectionId: string, itemId: string, nextTitle: string) => void;
  handleItemCreate: (sectionId: string, draft: { title: string; startDate: string; endDate?: string }) => void;
  handleItemDateRangeChange: (
    itemId: string,
    nextRange: { sectionId: string; startDate: string; endDate?: string },
  ) => void;
  handleItemDelete: (sectionId: string, itemId: string) => void;
  undoTimelineEdit: () => Promise<void>;
  redoTimelineEdit: () => Promise<void>;
}

export const useTimelineEditorActions = ({
  timelineId,
  timelineData,
  canUndo,
  canRedo,
  applyEditorState,
  setError,
  setErrorCode,
  setTimelineData,
  useCases,
}: UseTimelineEditorActionsParams): TimelineEditorActions => {
  const { t } = useTranslation('timeline');

  const handleActionError = useCallback((err: unknown, fallbackMessage: string) => {
    if (err instanceof TimelineConflictError && err.latestState) {
      applyEditorState(err.latestState);
      setError(null);
      setErrorCode(null);
      return;
    }
    if (err instanceof TimelineNotFoundError) {
      setErrorCode('not-found');
      setError(err.message);
      setTimelineData(null);
      return;
    }
    console.error(fallbackMessage, err);
  }, [applyEditorState, setError, setErrorCode, setTimelineData]);

  const executeEditorAction = useCallback(
    (action: (onOptimistic: OnOptimistic) => Promise<TimelineEditorState | null>, fallbackMessage: string) => {
      if (!timelineId) {
        return;
      }
      void action(applyEditorState).then(
        (nextState) => {
          if (!nextState) {
            return;
          }
          applyEditorState(nextState);
          setError(null);
          setErrorCode(null);
        },
        (err) => {
          handleActionError(err, fallbackMessage);
        },
      );
    },
    [applyEditorState, handleActionError, setError, setErrorCode, timelineId],
  );

  const addTimelineSection = useCallback(() => {
    executeEditorAction(
      (onOptimistic) => useCases.addSection(timelineId!, onOptimistic),
      t('error.addSectionFailed'),
    );
  }, [executeEditorAction, timelineId, useCases]);

  const handleSectionDelete = useCallback((sectionId: string) => {
    const section = timelineData?.pages[0].sections.find((l) => l.id === sectionId);
    if (section && section.items.length > 0) {
      const confirmed = window.confirm(
        t('error.deleteSectionConfirm', { label: section.label, count: section.items.length }),
      );
      if (!confirmed) {
        return;
      }
    }
    executeEditorAction(
      (onOptimistic) => useCases.removeSection(timelineId!, sectionId, onOptimistic),
      t('error.deleteSectionFailed'),
    );
  }, [executeEditorAction, timelineData, timelineId, useCases]);

  const handleSectionMove = useCallback((sectionId: string, toIndex: number) => {
    executeEditorAction(
      (onOptimistic) => useCases.moveSection(timelineId!, sectionId, toIndex, onOptimistic),
      t('error.moveSectionFailed'),
    );
  }, [executeEditorAction, timelineId, useCases]);

  const handleSectionLabelChange = useCallback((sectionId: string, nextLabel: string) => {
    executeEditorAction(
      (onOptimistic) => useCases.updateSectionLabel(timelineId!, sectionId, nextLabel, onOptimistic),
      t('error.updateSectionLabelFailed'),
    );
  }, [executeEditorAction, timelineId, useCases]);

  const handleSectionVisibilityToggle = useCallback((sectionId: string) => {
    const section = timelineData?.pages[0].sections.find((l) => l.id === sectionId);
    const currentHidden = section?.hidden ?? false;
    if (currentHidden) {
      executeEditorAction(
        (onOptimistic) => useCases.showSection(timelineId!, sectionId, onOptimistic),
        t('error.showSectionFailed'),
      );
    } else {
      executeEditorAction(
        (onOptimistic) => useCases.hideSection(timelineId!, sectionId, onOptimistic),
        t('error.hideSectionFailed'),
      );
    }
  }, [executeEditorAction, timelineData, timelineId, useCases]);

  const handleItemTitleChange = useCallback((sectionId: string, itemId: string, nextTitle: string) => {
    executeEditorAction(
      (onOptimistic) => useCases.updateItemTitle(timelineId!, sectionId, itemId, nextTitle, onOptimistic),
      t('error.updateItemTitleFailed'),
    );
  }, [executeEditorAction, timelineId, useCases]);

  const handleItemCreate = useCallback((sectionId: string, draft: { title: string; startDate: string; endDate?: string }) => {
    executeEditorAction(
      (onOptimistic) => useCases.createItem(timelineId!, sectionId, draft, onOptimistic),
      t('error.createItemFailed'),
    );
  }, [executeEditorAction, timelineId, useCases]);

  const handleItemDateRangeChange = useCallback((
    itemId: string,
    nextRange: { sectionId: string; startDate: string; endDate?: string },
  ) => {
    executeEditorAction(
      (onOptimistic) => useCases.changeItemDateRange(timelineId!, itemId, nextRange, onOptimistic),
      t('error.changeDateFailed'),
    );
  }, [executeEditorAction, timelineId, useCases]);

  const handleItemDelete = useCallback((sectionId: string, itemId: string) => {
    executeEditorAction(
      (onOptimistic) => useCases.deleteItem(timelineId!, sectionId, itemId, onOptimistic),
      t('error.deleteItemFailed'),
    );
  }, [executeEditorAction, timelineId, useCases]);

  const undoTimelineEdit = useCallback(async () => {
    if (!timelineId || !canUndo) {
      return;
    }
    try {
      const nextState = await useCases.undo(timelineId, applyEditorState);
      if (nextState) {
        applyEditorState(nextState);
      }
    } catch (err) {
      handleActionError(err, t('error.undoFailed'));
    }
  }, [applyEditorState, canUndo, handleActionError, timelineId, useCases]);

  const redoTimelineEdit = useCallback(async () => {
    if (!timelineId || !canRedo) {
      return;
    }
    try {
      const nextState = await useCases.redo(timelineId, applyEditorState);
      if (nextState) {
        applyEditorState(nextState);
      }
    } catch (err) {
      handleActionError(err, t('error.redoFailed'));
    }
  }, [applyEditorState, canRedo, handleActionError, timelineId, useCases]);

  return {
    addTimelineSection,
    handleSectionDelete,
    handleSectionMove,
    handleSectionLabelChange,
    handleSectionVisibilityToggle,
    handleItemTitleChange,
    handleItemCreate,
    handleItemDateRangeChange,
    handleItemDelete,
    undoTimelineEdit,
    redoTimelineEdit,
  };
};
